import * as TopList from 'components/Layout/Tiles/ListContainer';
import { Tile, TileContent } from 'components/Layout/PageContainer';
import { ANALYSIS_LEVEL_TO_ATTRIBUTE_CODE, AnalysisEntry, AnalysisLevel, MessageSentiment } from 'pages/trading/model';
import { AttributeCode, useModelService } from 'services/model/model.service';
import { FormattedMessage, useIntl } from 'react-intl';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';

import { Dialog, DialogActions, DialogContent, DialogTitle, Pagination } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TRADING_ROOT } from 'pages/trading/Trading';

import { MetricsSectionTitle } from 'components/MetricsSectionTitle';
import { AnalysisEntryBandChart } from 'pages/trading/outlier/AnalysisEntryBandChart';
import { TranslatedAttributeLabel } from 'pages/trading/TranslatedAttributeLabel';
import { ProductTopListNameCell } from 'pages/trading/outlier/ProductTopListNameCell';
import { Button } from 'components/Button';

interface ChildLevelOutliersDisplayProps {
  displayLevel: AnalysisLevel;
  entries: AnalysisEntry[];
  parentDiff: number;
  sentiment?: MessageSentiment;
}

interface ListItem extends AnalysisEntry {
  value: number;
  key: string;
  participationPercentage: number;
  barCount: number;
}

export function ChildLevelOutliersDisplay({
  displayLevel,
  entries,
  parentDiff,
  sentiment = 'NEUTRAL',
}: ChildLevelOutliersDisplayProps) {
  const { getMeasuresByMetrics } = useModelService();
  const [ordGmvVal] = getMeasuresByMetrics(['ordGmvVal', 'ordQty'], 'OD');
  const intl = useIntl();
  const PAGE_SIZE = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();

  const [showChildGraph, setShowChildGraph] = useState(false);
  const [selectedListItem, setSelectedListItem] = useState<ListItem>();

  const attributeCode: AttributeCode = ANALYSIS_LEVEL_TO_ATTRIBUTE_CODE[displayLevel][0];

  const driverLabels =
    sentiment === 'POSITIVE'
      ? {
          additionalTitleId: 'trading.title.additional.driver.positive',
          captionId: 'trading.title.driver.caption.positive',
          colourClassName: 'text-laurel-500',
        }
      : sentiment === 'NEGATIVE'
        ? {
            additionalTitleId: 'trading.title.additional.driver.negative',
            captionId: 'trading.title.driver.caption.negative',
            colourClassName: 'text-coral-500',
          }
        : {
            additionalTitleId: 'trading.title.additional.driver.neutral',
            captionId: 'trading.title.driver.caption.neutral',
            colourClassName: 'text-ash-300',
          };

  const showChildGraphDialog = (listItem: ListItem) => {
    setSelectedListItem(listItem);
    setShowChildGraph(true);
  };

  const hideChildGraphDialog = () => {
    setSelectedListItem(undefined);
    setShowChildGraph(false);
  };

  const getItemName = (analysisLevel: AnalysisLevel, levelKey: string) => {
    switch (analysisLevel) {
      case 'CATEGORY':
        return intl.formatMessage({ id: `product.categories.${levelKey}` });
      case 'COUNTRY':
        return (
          intl.formatDisplayName(levelKey.toUpperCase() ?? 'DE', {
            type: 'region',
          }) ?? ''
        );
      default:
        return levelKey;
    }
  };

  const getRowClickHandler = (analysisLevel: AnalysisLevel, levelKey: string): (() => void) | undefined => {
    switch (analysisLevel) {
      case 'CATEGORY':
      case 'COUNTRY':
      case 'MARKETPLACE':
      case 'BRAND':
      case 'PRODUCT':
        return () => {
          void navigate(`${TRADING_ROOT}/drilldown/${attributeCode}/${encodeURIComponent(levelKey)}${location.search}`);
        };
      default:
        return undefined;
    }
  };

  const createNameCell = (analysisLevel: AnalysisLevel, levelKey: string) => {
    const nameLabel = <TranslatedAttributeLabel attributeCode={attributeCode} attributeValue={levelKey} />;

    switch (analysisLevel) {
      case 'CATEGORY':
        return <TopList.NameCell hideColorBar icon={findIconForCategory(levelKey, 20)} name={nameLabel} />;
      case 'COUNTRY':
        return <TopList.NameCell hideColorBar countryCode={levelKey} name={nameLabel} />;
      case 'PRODUCT':
        return <ProductTopListNameCell productNumber={levelKey} />;
      case 'MARKETPLACE':
      case 'BRAND':
        return <TopList.NameCell hideColorBar name={nameLabel} />;
      default:
        return <TopList.NameCell hideColorBar name={nameLabel} />;
    }
  };

  const lastTimeSeriesEntry = (entry: AnalysisEntry) => entry.timeSeries[entry.timeSeries.length - 1];

  const listItems = entries
    .map((entry: AnalysisEntry) => {
      const lastTsEntry = lastTimeSeriesEntry(entry);
      const entryDiff = lastTsEntry.value - lastTsEntry.expectedValue;
      const participationPercentage = (entryDiff / parentDiff) * 100;
      const absPercentage = Math.abs(participationPercentage);
      let barCount = 1;
      if (absPercentage > 50) {
        barCount = 3;
      } else if (absPercentage > 30) {
        barCount = 2;
      }

      return {
        ...entry,
        attributeValue: getItemName(displayLevel, entry.attributeValue),
        key: entry.attributeValue,
        value: lastTsEntry.value,
        participationPercentage,
        barCount,
      };
    })
    .sort((a, b) => b.participationPercentage - a.participationPercentage);

  const getImpactColumn = (listItem: ListItem) => {
    const dots = Array.from({ length: 3 }, (_unused, index) => {
      const isFilled = index < listItem.barCount;
      return (
        <div
          key={`dot-${index}`}
          className={`w-4 h-4 rounded-full border-2 border-current ${isFilled ? 'bg-current' : 'bg-transparent'}`}
        />
      );
    });

    return (
      <td className="pl-6">
        <div className="grid grid-rows-2">
          <div className="text-xs flex items-center-safe">
            <span className="inline-block whitespace-nowrap leading-snug">
              <FormattedMessage id={driverLabels.captionId} />
            </span>
          </div>
          <div className={`flex flex-row items-center gap-2 min-h-6 ${driverLabels.colourClassName}`}>{dots}</div>
        </div>
      </td>
    );
  };

  const getSelectorColumn = (listItem: ListItem) => (
    <td className="pl-3 pr-3">
      <Button
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          showChildGraphDialog(listItem);
        }}
        icon="LineGraph"
      />
    </td>
  );

  useEffect(() => {
    const itemIdx = (currentPage - 1) * PAGE_SIZE;
    setSelectedListItem(itemIdx > listItems.length ? undefined : listItems[itemIdx]);
  }, [currentPage]);

  return (
    <>
      <Tile
        className="xl:col-span-6"
        titleId={`trading.analysis.level.header.${displayLevel}`}
        additionalTitle={<FormattedMessage id={driverLabels.additionalTitleId} />}
      >
        <TileContent className="flex flex-col justify-between items-center p-0">
          <TopList.ListContainer>
            {listItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map(entry => (
              <TopList.ListRow key={entry.key} onClick={getRowClickHandler(displayLevel, entry.key)}>
                {createNameCell(displayLevel, entry.key)}
                {getImpactColumn(entry)}
                <TopList.MetricCell measure={ordGmvVal} value={entry.value} unit={ordGmvVal.metric.unit} size="lg" />
                {getSelectorColumn(entry)}
              </TopList.ListRow>
            ))}
          </TopList.ListContainer>
          {Math.ceil(entries.length / PAGE_SIZE) > 1 && (
            <Pagination
              className="mt-4"
              size="small"
              count={Math.ceil(listItems.length / PAGE_SIZE)}
              page={currentPage}
              onChange={(_event, page) => setCurrentPage(page)}
            />
          )}
        </TileContent>
      </Tile>
      <Dialog open={showChildGraph} onClose={hideChildGraphDialog} maxWidth="xl">
        <DialogTitle id="child-modal" className=" pb-0 mb-3">
          <MetricsSectionTitle additionalTitle={<FormattedMessage id="trading.gmv.bandchart.title.overtime" />}>
            <FormattedMessage id="trading.gmv.bandchart.title" />
          </MetricsSectionTitle>
          <Button icon="Cross" aria-label="close" onClick={hideChildGraphDialog} className="ml-auto" tabIndex={0} />
        </DialogTitle>
        <DialogContent className="w-[800px]">
          <div className="px-4">
            <MetricsSectionTitle>
              {selectedListItem?.key && <FormattedMessage id={getItemName(displayLevel, selectedListItem.key)} />}
            </MetricsSectionTitle>
            {selectedListItem && (
              <div className="w-full">
                <AnalysisEntryBandChart selectedEntry={selectedListItem} measure={ordGmvVal} height="250px" />
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            priority="secondary"
            id="advanced-filter-dialog-cancel-button"
            onClick={() => setShowChildGraph(false)}
          >
            <FormattedMessage id="general.button.cancel" />
          </Button>
          <Button
            id="advanced-filter-dialog-apply-button"
            onClick={() => {
              navigate(`${TRADING_ROOT}/drilldown/${attributeCode}/${selectedListItem?.key ?? ''}${location.search}`);
            }}
          >
            <FormattedMessage id="trading.detail.view.button" defaultMessage="More Details" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
